import type { Image } from '@cocoonspace/sdk-js/types/image'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import { TwicImg } from '@twicpics/components/react'
import { useEffect, useMemo, useState } from 'react'
import {
	Carousel,
	type CarouselApi,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from '~/components/ui/carousel'
import type { SpaceCarouselPayload } from '~/domains/spaces/emitters/dialog.emitter'

const floorPlanFilter = (image: Image) => image.type === 'floor_plan'
const photoFilter = (image: Image) => image.type !== 'floor_plan'

export const SpaceImageCarousel = ({
	space,
	galleryOpts,
}: {
	space: Space
	galleryOpts?: SpaceCarouselPayload
}) => {
	const [api, setApi] = useState<CarouselApi>()
	const [index, setIndex] = useState(galleryOpts?.index || 0)

	const [options, setOptions] = useState({
		slidesToScroll: 1,
		loop: true,
		startIndex: galleryOpts?.index ?? 0,
	})

	useEffect(() => {
		if (!api) return

		api.on('select', () => {
			setIndex(api.selectedScrollSnap())
		})
	}, [api])

	const images = useMemo(
		() =>
			space.images?.filter(
				galleryOpts?.type === 'floor_plan' ? floorPlanFilter : photoFilter,
			),
		[space, galleryOpts],
	)

	return (
		<div className='flex w-full flex-col items-center justify-center lg:gap-8'>
			<Carousel
				opts={options}
				setApi={setApi}
				className='w-full max-w-screen-lg'
			>
				<CarouselContent>
					{images?.map((image, index) => (
						<CarouselItem key={index}>
							<TwicImg
								src={image.path}
								alt={space?.title}
								ratio='3/2'
								preTransform='quality=80/inside=1024x768'
							/>
						</CarouselItem>
					))}
				</CarouselContent>

				<CarouselPrevious />
				<CarouselNext />
			</Carousel>
			<p className='hidden text-center lg:block'>
				{index + 1}/{images?.length}
			</p>
		</div>
	)
}

export const SpaceDetailsImageCarousel = ({
	space,
	className,
	galleryOpts,
}: {
	space: Space
	galleryOpts?: SpaceCarouselPayload
	className?: string
	onClose?: () => void
}) => (
	<div className={className}>
		<div className='relative w-full'>
			<div className='flex items-center justify-center gap-x-10 lg:mb-10 lg:px-4'>
				<SpaceImageCarousel
					galleryOpts={galleryOpts}
					space={space}
				/>
			</div>
		</div>
	</div>
)
